/**
 * Conservative planning checks. Travel time is not proof of impossibility.
 * Previously resolved import conflicts stay dismissed.
 */

import type { TripDocument } from "./schema.ts";
import { instantMs } from "./time.ts";

export type PlanningFlag = {
  id: string;
  severity: "info" | "watch";
  title: string;
  detail: string;
  entityIds: string[];
};

export function planningFlags(doc: TripDocument): PlanningFlag[] {
  const flags: PlanningFlag[] = [];
  const dismissed = new Set(doc.reviewItems.filter((i) => i.dismissed).map((i) => i.id));

  const boat = doc.reviewItems.find((i) => i.id === "boats");
  if (boat && !boat.dismissed) {
    flags.push({
      id: "boats",
      severity: "watch",
      title: "Boat crossing not booked in this pack",
      detail: "The Kasem van ends at the mainland pier. Boat tickets and the exact pier remain unknown.",
      entityIds: boat.entityIds,
    });
  }

  const kasemReturn = doc.events.find((e) => e.id === "kasem-return");
  if (kasemReturn && !kasemReturn.plannedDeparture && !dismissed.has("kasem-return")) {
    flags.push({
      id: "kasem-return-time",
      severity: "watch",
      title: "Return van time to confirm",
      detail: "6 November is inferred from hotel dates. Pickup time and exact pier are not in the accepted chat.",
      entityIds: ["kasem-return"],
    });
  }

  const food = doc.events.find((e) => e.id === "food-tour-event");
  if (food?.startConfidence?.includes("provisional") && !dismissed.has("food-meeting")) {
    flags.push({
      id: "food-meeting",
      severity: "info",
      title: "Food-tour meeting remains provisional",
      detail: "16:00 at Warorot Market comes from a generic webpage screenshot, not a custom-tour confirmation.",
      entityIds: ["food-tour-event"],
    });
  }

  for (const stay of doc.stays) {
    const hotelBookings = doc.bookings.filter((b) => b.stayId === stay.id && b.kind === "hotel_reservation");
    if (hotelBookings.length === 0) {
      flags.push({
        id: `stay-booking-${stay.id}`,
        severity: "watch",
        title: `No reservation linked to ${stay.chapter}`,
        detail: "The stay dates exist without a matching booking record.",
        entityIds: [stay.id],
      });
    }
  }

  const flights = [...doc.flights].sort((a, b) => instantMs(a.departure.instant) - instantMs(b.departure.instant));
  for (let i = 1; i < flights.length; i += 1) {
    if (instantMs(flights[i].departure.instant) < instantMs(flights[i - 1].arrival.instant)) {
      flags.push({
        id: `chrono-${flights[i].id}`,
        severity: "watch",
        title: "Flight order looks impossible",
        detail: `${flights[i].number} departs before ${flights[i - 1].number} arrives.`,
        entityIds: [flights[i - 1].id, flights[i].id],
      });
    }
  }

  return flags.filter((flag) => !dismissed.has(flag.id));
}
